import cookies from 'js-cookie'
import { useCallback, useState } from 'react'

import { ERROR_ACCEPT_INVITATION_WITH_SIGNUP, SUCCESS_AUTH_SIGNUP } from '@/app/module/auth/definitions'
import { setToken } from '@/app/module/auth/helpers'
import { getUserPreferences } from '@/app/module/auth/store/session.slice'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser } from '@/app/module/user/store/actions'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export type AcceptInvitationSignupParams = {
  invitationToken: string
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  country?: string
  organization?: string
  organizationType?: string
  verifyToken?: string
}

export const useAcceptInvitationSignup = () => {
  const dispatch = useAppDispatch()
  const [inviteSignupAccept, mutationResult] = esApi.endpoints.inviteSignupAccept.useMutation()
  const [isLoading, setIsLoading] = useState(false)

  const acceptInvitationWithSignup = useCallback(
    async (params: AcceptInvitationSignupParams) => {
      setIsLoading(true)

      try {
        const body = {
          token: params.invitationToken,
          email: params.email,
          password: params.password,
          first_name: params.firstName,
          last_name: params.lastName,
          phone: params.phone || '',
          country: params.country || '',
          organization: params.organization || '',
          organization_type: params.organizationType || '',
          verify_token: params.verifyToken || '',
        }

        const response = await inviteSignupAccept({ body }).unwrap()

        dispatch(
          createNotification({
            'signup-success': { type: 'success', message: SUCCESS_AUTH_SIGNUP, disableAutoHide: true },
          }),
        )

        const authToken = response.access
        setToken(authToken, response.validUntil)

        const user = await dispatch(getUser({ token: authToken }))
        if (user && !(user instanceof Error)) {
          dispatch(getUserPreferences({ user, token: authToken }))
        }

        cookies.remove('redirectURL')

        return { user, authToken }
      } catch (error) {
        const errorMessage = getErrorMessage(error, ERROR_ACCEPT_INVITATION_WITH_SIGNUP)
        dispatch(
          createNotification({
            'accept-invitation-signup-error': {
              type: 'error',
              message: errorMessage,
            },
          }),
        )
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, inviteSignupAccept],
  )

  return [acceptInvitationWithSignup, { ...mutationResult, isLoading }] as const
}
